import FileSaver from 'file-saver';

import { OrderDetail } from '@customTypes/order';
import { Requisition } from '@customTypes/requisition';
import { FamilyHistory, PatientOrderingProduct } from '@customTypes/test-ordering';
import axios from '@hooks/useAxios';

const PATIENT_PORTAL_BASE_URL = '/patient-portal';

export interface PatientRerequisitionNewOrder {
  is_eligible: boolean;
  products: PatientOrderingProduct[];
}

export interface PatientRequisitionListResponse {
  requisitions: Requisition[];
  new_order: PatientRerequisitionNewOrder | null;
}

interface LegacyPatientRequisitionListResponse {
  results: Requisition[];
}

export interface PatientRerequisitionEligibilityResponse {
  is_eligible: boolean;
  new_order: {
    price_cents: number;
    products: PatientOrderingProduct[];
  } | null;
}

export interface PatientRerequisitionSubmitRequest {
  consents_granted: string[];
  family_history: FamilyHistory;
  individual?: string;
  payment_intent_id: string | null;
  product_ids: string[];
}

export interface PatientRerequisitionSubmitResponse {
  requisition_id: string;
}

export interface GetOrderResultsParams {
  orderId: string;
  expand?: boolean;
  individual?: string;
}

export const fetchOpenRequisitions = async (individualId?: string): Promise<PatientRequisitionListResponse> => {
  return axios
    .get(`${PATIENT_PORTAL_BASE_URL}/requisition`, {
      params: { individual: individualId },
      suppressGlobalErrorToast: true,
    })
    .then(res => {
      const response = res.data as PatientRequisitionListResponse | LegacyPatientRequisitionListResponse;
      if ('requisitions' in response) return response;

      return {
        requisitions: response.results,
        new_order: null,
      };
    });
};

export const fetchRerequisitionEligibility = async (
  individualId?: string,
): Promise<PatientRerequisitionEligibilityResponse> => {
  return axios
    .post(`${PATIENT_PORTAL_BASE_URL}/rerequisition/eligibility`, undefined, {
      params: { individual: individualId },
      suppressGlobalErrorToast: true,
    })
    .then(res => res.data);
};

export const submitRerequisition = async (
  payload: PatientRerequisitionSubmitRequest,
): Promise<PatientRerequisitionSubmitResponse> => {
  return axios.post(`${PATIENT_PORTAL_BASE_URL}/rerequisition/submit`, payload).then(res => res.data);
};

export const getOrderResultsById = async ({
  orderId,
  expand = false,
  individual,
}: GetOrderResultsParams): Promise<OrderDetail> => {
  return axios
    .get(`${PATIENT_PORTAL_BASE_URL}/order/${orderId}`, {
      params: { expand, individual },
    })
    .then(res => res.data);
};

export const downloadAllResults = async (individual?: string): Promise<void> => {
  const response = await axios.get(`${PATIENT_PORTAL_BASE_URL}/results/download`, {
    params: { individual },
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  FileSaver.saveAs(blob, 'All Results.pdf');
};
